import React from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import moment from 'moment'
import PersonAndOrganisationSearch from '../Inputs/PersonAndOrganisationSearch'
import ActivityReservationConflictDialog from '../Dialogs/ActivityReservationConflictDialog'
import {
  buildReservationSummary,
  defaultPayersForActivity,
  formatCollision,
  formatReservationTime,
  isAttached,
} from '../utils/activityReservations'

const parseAmount = (raw) => {
  const cleaned = String(raw).replace(',', '.').replace(/[^0-9.]/g, '')
  const parsed = parseFloat(cleaned)
  return isFinite(parsed) ? parsed : 0
}

const payerName = (payer) => {
  if (!payer) return ''
  if (payer.name) return payer.name
  return [payer.firstname, payer.lastname].filter(Boolean).join(' ')
}

/**
 * The Varaukset tab: book the courts an activity occupies, or attach bookings that
 * already exist. Creates one reservation per resource -- pricing and conflict
 * checking are both per-resource in the backend, so a single multi-resource
 * reservation would only ever be checked on its first court.
 *
 * `activity` is the SAVED activity, not the edited one: attaching a court must not
 * quietly commit half-finished edits from the Yleistiedot tab.
 */
function ActivityReservations({ activity, resources, firebase, onReservationsChange }) {
  const attachedReservations = React.useMemo(() => activity.reservations || [], [activity.reservations])

  const [selectedResources, setSelectedResources] = React.useState([])
  const [payers, setPayers] = React.useState(defaultPayersForActivity(activity))
  const [costInput, setCostInput] = React.useState('0,00')
  const [conflicts, setConflicts] = React.useState([])
  const [checkingConflicts, setCheckingConflicts] = React.useState(false)
  const [listPrice, setListPrice] = React.useState(null)
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState(null)

  const [dayReservations, setDayReservations] = React.useState([])
  const [loadingDay, setLoadingDay] = React.useState(false)
  const [selectedExisting, setSelectedExisting] = React.useState([])

  const hasTimes = !!(activity.start && activity.end)

  // A resource already attached cannot be booked again from here.
  const attachedResourceIds = attachedReservations.map((r) => r.resourceid)
  const availableResources = (resources || []).filter((r) => !attachedResourceIds.includes(r.id))

  const runConflictCheck = React.useCallback(
    async (picked) => {
      if (!hasTimes || !picked || picked.length === 0) {
        setConflicts([])
        return
      }
      setCheckingConflicts(true)
      const result = await firebase.checkResourceConflicts(activity.start, activity.end, picked)
      setCheckingConflicts(false)
      if (!result || result.error) {
        setError('Päällekkäisyyksien tarkistus epäonnistui.')
        setConflicts([])
        return
      }
      setError(null)
      setConflicts(result.filter((row) => row.isconflict))
    },
    [activity.start, activity.end, firebase, hasTimes]
  )

  const fetchListPrice = React.useCallback(
    async (picked) => {
      if (!hasTimes || !picked || picked.length === 0) {
        setListPrice(null)
        return
      }
      // Note the casing: pricing wants resourceId, the conflict call wants resourceid.
      const slot = {
        start: moment.utc(activity.start).format('YYYY-MM-DD HH:mm'),
        end: moment.utc(activity.end).format('YYYY-MM-DD HH:mm'),
        resourceId: picked[0].id,
      }
      const response = await firebase.getReservationPrices(slot, null)
      const prices = response && response.data ? response.data.prices : null
      const general = prices ? prices.find((p) => p.pricing === 'general') : null
      setListPrice(general ? general.price : null)
    },
    [activity.start, activity.end, firebase, hasTimes]
  )

  const handleResourceChange = (event, value) => {
    setSelectedResources(value)
    runConflictCheck(value)
    fetchListPrice(value)
  }

  const loadDayReservations = React.useCallback(async () => {
    if (!hasTimes) return
    setLoadingDay(true)
    try {
      const entries = await firebase.getReservationsForDay(moment.utc(activity.start).format('YYYY-MM-DD'))
      const usable = entries
        .filter((e) => !e.data || e.data.state !== 'cancelled')
        // A recurring reservation is one document covering the whole series, so linking
        // it would reach every occurrence, not just this activity's. Not offered.
        .filter((e) => !e.data || !e.data.isrecurring)
        .filter((e) => !isAttached(attachedReservations, e.id))
        .sort((a, b) => String(a.start).localeCompare(String(b.start)))
      setDayReservations(usable)
    } catch (e) {
      setError('Päivän varausten haku epäonnistui.')
    }
    setLoadingDay(false)
  }, [activity.start, attachedReservations, firebase, hasTimes])

  React.useEffect(() => {
    loadDayReservations()
  }, [loadDayReservations])

  const persist = async (nextReservations) => {
    await onReservationsChange(nextReservations)
  }

  const doCreate = async () => {
    setSaving(true)
    setError(null)
    const result = await firebase.createActivityReservations(activity, selectedResources, payers, {
      cost: parseAmount(costInput),
    })
    if (result.errors.length > 0) {
      setError(
        'Osaa varauksista ei voitu luoda: ' +
          result.errors.map((e) => (e.resource ? e.resource.displayName : '?')).join(', ')
      )
    }
    if (result.created.length > 0) {
      await persist([...attachedReservations, ...result.created])
    }
    setSelectedResources([])
    setConflicts([])
    setConfirmOpen(false)
    setSaving(false)
    loadDayReservations()
  }

  const handleReserve = () => {
    if (conflicts.length > 0) {
      setConfirmOpen(true)
      return
    }
    doCreate()
  }

  const handleDetach = async (reservation) => {
    setSaving(true)
    await firebase.detachReservationFromActivity(reservation.id)
    await persist(attachedReservations.filter((r) => r.id !== reservation.id))
    setSaving(false)
    loadDayReservations()
  }

  const handleAttachExisting = async () => {
    setSaving(true)
    setError(null)
    const added = []
    for (let i = 0; i < selectedExisting.length; i++) {
      const entry = dayReservations.find((e) => e.id === selectedExisting[i])
      if (!entry) continue
      const result = await firebase.attachReservationToActivity(activity.id, entry.id)
      if (result && result.error) {
        setError('Varauksen liittäminen epäonnistui.')
        continue
      }
      const resource = (resources || []).find((r) => r.id === entry.resourceId)
      added.push(buildReservationSummary(entry, resource, 'existing'))
    }
    if (added.length > 0) await persist([...attachedReservations, ...added])
    setSelectedExisting([])
    setSaving(false)
    loadDayReservations()
  }

  const toggleExisting = (id) => {
    setSelectedExisting((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  if (!hasTimes) {
    return (
      <Alert severity='info'>
        Aseta aktiviteetille alku- ja loppuaika Yleistiedot-välilehdellä ennen kenttien varaamista.
      </Alert>
    )
  }

  return (
    <Box>
      {error ? (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Typography variant='subtitle1' gutterBottom>
        Varaa kentät aktiviteetille
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        {formatReservationTime({ start: activity.start, end: activity.end })} — jokaisesta valitusta
        resurssista tehdään oma varaus.
      </Typography>

      {/* PersonAndOrganisationSearch is shared with ReservationModal and renders a
          standard-variant field with margin='normal'. The other two match it rather
          than the reverse, so all three labels and input lines sit on one baseline.
          flex-grow stays 0 so the fields keep a sensible width on a wide screen
          instead of stretching across it; they still shrink and wrap when narrow. */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: '0 1 320px', minWidth: 200 }}>
          <Autocomplete
            multiple
            options={availableResources}
            value={selectedResources}
            onChange={handleResourceChange}
            getOptionLabel={(option) => option.displayName || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Varattavat resurssit'
                variant='standard'
                margin='normal'
                fullWidth
              />
            )}
          />
        </Box>
        <Box sx={{ flex: '0 1 240px', minWidth: 180 }}>
          <PersonAndOrganisationSearch
            label='Maksaja'
            placeholder='Maksaja'
            values={payers}
            onChange={(event, value) => setPayers(value || [])}
          />
        </Box>
        <Box sx={{ flex: '0 0 160px' }}>
          <TextField
            label='Hinta'
            variant='standard'
            margin='normal'
            fullWidth
            value={costInput}
            onChange={(e) => setCostInput(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position='end'>€</InputAdornment> }}
          />
          {listPrice !== null ? (
            <Chip
              size='small'
              color='info'
              label={'Hinnasto ' + listPrice + ' €'}
              onClick={() => setCostInput(String(listPrice).replace('.', ','))}
              sx={{ cursor: 'pointer' }}
            />
          ) : null}
        </Box>
      </Box>

      {checkingConflicts ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={14} />
          <Typography variant='body2'>Tarkistetaan päällekkäisyydet...</Typography>
        </Box>
      ) : conflicts.length > 0 ? (
        <Alert severity='warning' sx={{ mb: 2 }}>
          {conflicts.map((row, index) => (
            <div key={index}>
              <b>{row.resource ? row.resource.displayName : ''}</b> on jo varattu
              {row.collision ? ' - ' + formatCollision(row.collision) : ''}
            </div>
          ))}
        </Alert>
      ) : selectedResources.length > 0 ? (
        <Typography variant='body2' color='success.main' sx={{ mb: 2 }}>
          Ei päällekkäisyyksiä.
        </Typography>
      ) : null}

      <Button
        variant='contained'
        color='primary'
        disabled={selectedResources.length === 0 || payers.length === 0 || saving || checkingConflicts}
        onClick={handleReserve}
      >
        {saving ? 'Varataan...' : 'Varaa'}
      </Button>
      {payers.length === 0 && selectedResources.length > 0 ? (
        <Typography variant='caption' color='text.secondary' sx={{ ml: 2 }}>
          Varaus tarvitsee maksajan.
        </Typography>
      ) : null}

      <Divider sx={{ my: 3 }} />

      <Typography variant='subtitle1' gutterBottom>
        Liitetyt varaukset
      </Typography>
      {attachedReservations.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          Aktiviteettiin ei ole liitetty varauksia.
        </Typography>
      ) : (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Resurssi</TableCell>
              <TableCell>Aika</TableCell>
              <TableCell>Hinta</TableCell>
              <TableCell>Liitetty</TableCell>
              <TableCell align='right'> </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {attachedReservations.map((reservation) => (
              <TableRow key={reservation.id}>
                <TableCell>{reservation.resourcename || reservation.resourceid}</TableCell>
                <TableCell>{formatReservationTime(reservation)}</TableCell>
                <TableCell>{(Number(reservation.cost) || 0).toFixed(2).replace('.', ',')} €</TableCell>
                <TableCell>
                  <Chip
                    size='small'
                    variant='outlined'
                    label={reservation.attached === 'existing' ? 'Olemassa ollut' : 'Luotu tästä'}
                  />
                </TableCell>
                <TableCell align='right'>
                  <IconButton
                    size='small'
                    title='Irrota'
                    disabled={saving}
                    onClick={() => handleDetach(reservation)}
                  >
                    <LinkOffIcon fontSize='small' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant='subtitle1' gutterBottom>
        Liitä olemassa oleva varaus
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
        {moment.utc(activity.start).format('dd D.M.YYYY')} varaukset
      </Typography>
      {loadingDay ? (
        <CircularProgress size={16} />
      ) : dayReservations.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          Päivälle ei löytynyt liitettäviä varauksia.
        </Typography>
      ) : (
        <>
          <Table size='small'>
            <TableBody>
              {dayReservations.map((entry) => {
                const resource = (resources || []).find((r) => r.id === entry.resourceId)
                const payer =
                  entry.data && entry.data.persons && entry.data.persons.payers
                    ? payerName(entry.data.persons.payers[0])
                    : ''
                return (
                  <TableRow key={entry.id} hover>
                    <TableCell padding='checkbox'>
                      <Checkbox
                        checked={selectedExisting.includes(entry.id)}
                        onChange={() => toggleExisting(entry.id)}
                      />
                    </TableCell>
                    <TableCell>{resource ? resource.displayName : entry.resourceId}</TableCell>
                    <TableCell>{formatReservationTime(entry)}</TableCell>
                    <TableCell>{entry.title || payer}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          <Button
            sx={{ mt: 1 }}
            variant='outlined'
            disabled={selectedExisting.length === 0 || saving}
            onClick={handleAttachExisting}
          >
            Liitä valitut
          </Button>
        </>
      )}

      <ActivityReservationConflictDialog
        open={confirmOpen}
        conflicts={conflicts}
        saving={saving}
        onClose={() => setConfirmOpen(false)}
        onConfirm={doCreate}
      />
    </Box>
  )
}

export default ActivityReservations
